import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { LineItemReorderActionStatusContextValue } from '../../types/LineItemReorderActionStatus.types';

interface LineItemReorderActionStatusProviderProps {
  children: ReactNode;
}

const LineItemReorderActionStatusContext =
  createContext<LineItemReorderActionStatusContextValue | null>(null);

/**
 * This context manages the state of the given reorder "action" (i.e. add to cart, edit design & reorder)
 */
const LineItemReorderActionStatusProvider = ({
  children,
}: LineItemReorderActionStatusProviderProps) => {
  const [action, setAction] = useState('');
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const [addToCartFailure, setAddToCartFailure] = useState(false);
  const [designUpdateInProgress, setDesignUpdateInProgress] = useState(false);
  const [productUnavailableFailure, setProductUnavailableFailure] =
    useState(false);
  const [docConversionFailure, setDocConversionFailure] = useState(false);
  const [genericFailure, setGenericFailure] = useState(false);
  const [altDocId, setAltDocId] = useState('');

  // Resets the state to track the currently engaged action
  const resetReorderActionFeedback = (onClickAction?: string) => {
    setAction(onClickAction || '');
    setAddToCartSuccess(false);
    setAddToCartFailure(false);
    setDesignUpdateInProgress(false);
    setProductUnavailableFailure(false);
    setDocConversionFailure(false);
    setGenericFailure(false);
    setAltDocId('');
  };

  const lineItemReorderActionStatusContextValues = useMemo(
    () => ({
      action,
      addToCartSuccess,
      setAddToCartSuccess,
      addToCartFailure,
      setAddToCartFailure,
      designUpdateInProgress,
      setDesignUpdateInProgress,
      resetReorderActionFeedback,
      productUnavailableFailure,
      setProductUnavailableFailure,
      docConversionFailure,
      setDocConversionFailure,
      genericFailure,
      setGenericFailure,
      altDocId,
      setAltDocId,
    }),
    [
      action,
      addToCartFailure,
      addToCartSuccess,
      altDocId,
      designUpdateInProgress,
      docConversionFailure,
      genericFailure,
      productUnavailableFailure,
    ]
  );

  return (
    <LineItemReorderActionStatusContext.Provider
      value={lineItemReorderActionStatusContextValues}
    >
      {children}
    </LineItemReorderActionStatusContext.Provider>
  );
};

const useLineItemReorderActionStatus =
  (): LineItemReorderActionStatusContextValue => {
    const context = useContext(LineItemReorderActionStatusContext);
    if (!context) {
      throw new Error(
        'useLineItemReorderActionStatus must be used within an LineItemReorderActionStatusProvider'
      );
    }
    return context;
  };

export {
  LineItemReorderActionStatusContext,
  LineItemReorderActionStatusProvider,
  useLineItemReorderActionStatus,
};
